// src/components/ProtectedRoute.js

import React, { useEffect, useState } from "react";
import {
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  onAuthStateChanged,
} from "firebase/auth";
import { educationAuth } from "../firebase.js";

function ProtectedRoute({ children }) {
  const location = useLocation();

  const [educationUser, setEducationUser] =
    useState(null);
  const [loading, setLoading] = useState(true);

  /*
  |--------------------------------------------------------------------------
  | VÉRIFICATION DE LA SESSION ÉDUCATION
  |--------------------------------------------------------------------------
  |
  | Ce composant utilise uniquement educationAuth.
  |
  | Une session ouverte dans Consulting ou Finance ne permet donc plus
  | d’accéder aux pages protégées de HDC Éducation.
  |
  */

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      educationAuth,
      async (currentUser) => {
        if (!currentUser) {
          setEducationUser(null);
          setLoading(false);
          return;
        }

        try {
          /*
           * Recharge les informations du compte pour obtenir
           * l’état actuel de la vérification de l’adresse e-mail.
           */
          await currentUser.reload();

          setEducationUser(
            educationAuth.currentUser || currentUser
          );
        } catch (error) {
          console.error(
            "Erreur lors de la vérification du compte Éducation :",
            error
          );

          setEducationUser(currentUser);
        } finally {
          setLoading(false);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  /*
  |--------------------------------------------------------------------------
  | ÉCRAN DE CHARGEMENT
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.loader} />

        <p style={styles.loadingText}>
          Vérification de votre accès Éducation…
        </p>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | UTILISATEUR NON CONNECTÉ À ÉDUCATION
  |--------------------------------------------------------------------------
  |
  | On conserve l’adresse demandée afin de pouvoir rediriger
  | l’utilisateur après sa connexion.
  |
  */

  if (!educationUser) {
    const returnTo = `${location.pathname}${location.search}`;

    return (
      <Navigate
        to="/connexion"
        replace
        state={{ returnTo }}
      />
    );
  }

  /*
  |--------------------------------------------------------------------------
  | ADRESSE E-MAIL NON VÉRIFIÉE
  |--------------------------------------------------------------------------
  */

  if (
    educationUser.email &&
    !educationUser.emailVerified
  ) {
    const returnTo = `${location.pathname}${location.search}`;

    return (
      <Navigate
        to="/connexion"
        replace
        state={{
          returnTo,
          verificationRequired: true,
        }}
      />
    );
  }

  /*
  |--------------------------------------------------------------------------
  | ACCÈS AUTORISÉ
  |--------------------------------------------------------------------------
  */

  return children;
}

const styles = {
  loadingPage: {
    minHeight: "70vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
    background:
      "linear-gradient(145deg, #f8fafc, #eef2ff)",
    color: "#0f172a",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  loader: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "4px solid rgba(124, 58, 237, 0.16)",
    borderTopColor: "#7c3aed",
    animation: "spin 0.8s linear infinite",
  },

  loadingText: {
    margin: 0,
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "700",
  },
};

export default ProtectedRoute;